/**
 * In-app deep links from AskBot answers to the pages that own each record.
 *
 * WHY THIS LIVES IN ONE PLACE
 * ---------------------------
 * Two different vocabularies meet here. The chatbot emits canonical snake_case
 * loan-type keys (`chatbot/core/aliases.ts` → `normalizeLoanType`: "home_loan",
 * "lap"), while the pages we link into select on the display labels that come
 * out of `config.bankLoanTypeLocations` ("Home Loan", "Loan Against Property
 * (LAP)"). Every link therefore needs the same translation, and getting it
 * wrong lands the user on a blank picker instead of the record they tapped.
 *
 * TWO LINK SHAPES
 * ---------------
 *  • Id-addressed  — bankers / channel partners / DSA partners have real
 *    Firestore doc ids in the AskBot rows and matching `:id` routes, so the
 *    link is exact.
 *  • Selection-addressed — payout grids and product notes have no `:id` route.
 *    Those pages are step-cascades (city → loan type → lender) holding local
 *    state, so we pass the selection as query params and let the page seed
 *    itself as deep as the values actually match. A param that matches nothing
 *    simply leaves the user on that step rather than breaking the page.
 */

/** Sentinel understood by ManualMatcher for "no city filter". */
export const ALL_CITIES = "__all__";

/** Which notes tab a product link should open on. */
export type ProductNotesTab = "product" | "sales" | "files";

// ---------------------------------------------------------------------------
// Value matching
// ---------------------------------------------------------------------------

/**
 * Loose key for comparing a loan type written either way. Parenthetical
 * suffixes are dropped first so the config label "Loan Against Property (LAP)"
 * reduces to the same key as the chatbot's "lap".
 *
 * Deliberately not `normalizeLoanType` from @/types: that one only handles the
 * canonical enum keys and their plain-label spellings, and returns the input
 * untouched for anything else — including every parenthesised config label.
 */
const LOAN_TYPE_KEY_ALIASES: Record<string, string> = {
  loan_against_property: "lap",
  property_loan: "lap",
  home_loan_lap: "lap",
};

function loanTypeKey(value: string): string {
  const base = value
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ") // drop "(LAP)" and friends
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return LOAN_TYPE_KEY_ALIASES[base] ?? base;
}

function plainKey(value: string): string {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * Finds the option a deep-link param refers to, comparing case- and
 * spacing-insensitively. Returns the option *as the page spells it*, which is
 * what the page's own state and equality checks expect.
 */
export function matchOption(
  available: readonly string[],
  raw: string | undefined | null,
): string | undefined {
  if (!raw) return undefined;
  const want = plainKey(raw);
  return available.find((opt) => plainKey(opt) === want);
}

/** As `matchOption`, but tolerant of key-vs-label loan type spellings. */
export function matchLoanTypeOption(
  available: readonly string[],
  raw: string | undefined | null,
): string | undefined {
  if (!raw) return undefined;
  const exact = matchOption(available, raw);
  if (exact) return exact;
  const want = loanTypeKey(raw);
  return available.find((opt) => loanTypeKey(opt) === want);
}

// ---------------------------------------------------------------------------
// Link builders
// ---------------------------------------------------------------------------

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v).trim();
}

function query(params: Record<string, string>): string {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== ""),
  ).toString();
  return qs ? `?${qs}` : "";
}

/** `/tools/payouts` pre-seeded to one grid. */
export function buildPayoutLink(row: Record<string, unknown>): string | null {
  const lender = str(row.lender ?? row.bank);
  const loanType = str(row.loanType ?? row.loan_type);
  // A national grid has no location; the page then opens on the city step.
  const location = str(row.city ?? row.location);
  if (!lender && !loanType) return null;
  return `/tools/payouts${query({ location, loanType, lender })}`;
}

/**
 * `/products` pre-seeded to one bank + loan type.
 *
 * The product track resolves a bank and a loan type but never a city, while
 * the notes panel refuses to render without all three — so an unresolved city
 * falls back to the ALL_CITIES sentinel rather than stranding the user on the
 * city step with nothing selected.
 */
export function buildProductNotesLink(opts: {
  bank?: string | null;
  loanType?: string | null;
  city?: string | null;
  tab?: ProductNotesTab;
}): string | null {
  const bank = str(opts.bank);
  const loanType = str(opts.loanType);
  if (!bank || !loanType) return null;
  return `/products${query({
    city: str(opts.city) || ALL_CITIES,
    loanType,
    lender: bank,
    tab: opts.tab ?? "",
  })}`;
}

/**
 * The route an AskBot result row opens, or null when this row isn't something
 * the app has a page for. `entity` is the label from `DIRECTORY_ENTITY` in
 * services/askbotService.ts.
 */
export function buildRecordLink(
  entity: string | undefined,
  row: Record<string, unknown>,
): string | null {
  const id = str(row.id);

  switch (entity) {
    case "bankers":
      return id ? `/tools/sm-contact/${id}` : null;
    case "channelPartners":
    case "vendors":
      return id ? `/tools/channel-partners/${id}` : null;
    case "dsaPartners":
      return id ? `/tools/dsa-partners/${id}` : null;
    case "payoutRates":
      return buildPayoutLink(row);
    case "products":
      return buildProductNotesLink({
        bank: str(row.bank ?? row.lender),
        loanType: str(row.loanType ?? row.category),
        city: str(row.city),
      });
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Inbound deep links (WhatsApp → app)
// ---------------------------------------------------------------------------

/**
 * Routes an inbound deep link may name.
 *
 * Kept identical to `ALLOWED_ROUTE_PREFIXES` in `chatbot/core/deeplinks.ts` and
 * the copy in `public/open.html`. The `to` param arrives from a URL a stranger
 * can craft, so it is untrusted: without this check the link is an open redirect
 * into the app shell. See docs/deep-links.md.
 */
export const ALLOWED_ROUTE_PREFIXES = [
  "/tools/sm-contact",
  "/tools/channel-partners",
  "/tools/dsa-partners",
  "/tools/payouts",
  "/products",
] as const;

export function isAllowedRoute(route: string): boolean {
  if (!route.startsWith("/") || route.startsWith("//")) return false;
  const path = route.split("?")[0];
  return ALLOWED_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Pulls the in-app route out of a deep link, or null if there isn't a usable one.
 *
 * Handles both shapes the OS can hand us: the verified App/Universal Link
 * (`https://<host>/open?to=…`) and the interstitial's custom scheme
 * (`dsalens://open?to=…`). Both carry the route in `to` rather than in their own
 * path, so one parser covers them — see docs/deep-links.md for why the link is
 * shaped that way.
 */
export function parseDeepLink(url: string): string | null {
  let to: string | null;
  try {
    to = new URL(url).searchParams.get("to");
  } catch {
    return null;
  }
  if (!to) return null;
  return isAllowedRoute(to) ? to : null;
}
